import { apiUrl } from '../utils/api-url';
import type { MigrateContext } from './project-service';

/**
 * Client for Azure Migrate assessment runs (orchestrator RFC §4.3) and the
 * project MigrateContext block. The import endpoint is the browser-upload half
 * of the architect-workstation execution path.
 */

export interface MigrateRunCostSummary {
  paygMonthly?: number | null;
  currency?: string;
}

export interface MigrateRunSourceFile {
  fileName?: string;
  sha256?: string;
  sizeBytes?: number;
  uploadedBy?: string | null;
}

export interface MigrateRun {
  id: string;
  customerId: string;
  projectId: string;
  runNumber: number;
  kind: string;
  executionPath: string;
  status: string;
  strategyName?: string | null;
  confidencePercent?: number | null;
  confidenceStars?: number | null;
  perfDataDays?: number | null;
  machineCount: number;
  costSummary?: MigrateRunCostSummary | null;
  sourceFile?: MigrateRunSourceFile | null;
  parseWarnings?: string[];
  resultArtifactId?: string | null;
  supersedesRunId?: string | null;
  diffArtifactId?: string | null;
  createdAt: string;
  createdBy?: string | null;
  completedAt?: string | null;
}

export interface ServerRef {
  serverKey: string;
  name: string;
}

export interface ServerFieldChange {
  serverKey: string;
  name: string;
  before?: string | null;
  after?: string | null;
}

export interface ServerCostChange {
  serverKey: string;
  name: string;
  beforeMonthly: number;
  afterMonthly: number;
  deltaPercent?: number | null;
}

export interface OverrideConflict {
  serverKey: string;
  name: string;
  workloadId: string;
  workloadName?: string | null;
  propertyPath: string;
  architectValue?: string | null;
  deviationBaselineValue?: string | null;
  newAssessmentValue?: string | null;
  stale: boolean;
}

export interface MigrateRunDiffSummary {
  matchedServers: number;
  serversAdded: number;
  serversMissing: number;
  sizeChanges: number;
  readinessTransitions: number;
  costChanges: number;
  conflictsWithOverrides: number;
  paygMonthlyDelta?: number | null;
  paygMonthlyDeltaPercent?: number | null;
  currency?: string | null;
  confidenceStarsDelta?: number | null;
}

export interface MigrateRunDiff {
  id: string;
  runId: string;
  baseRunId: string;
  crossScope: boolean;
  crossScopeReason?: string | null;
  summary: MigrateRunDiffSummary;
  sizeChanges: ServerFieldChange[];
  readinessTransitions: ServerFieldChange[];
  costChanges: ServerCostChange[];
  serversAdded: ServerRef[];
  serversMissing: ServerRef[];
  conflictsWithOverrides: OverrideConflict[];
  generatedAt: string;
}

export interface ImportOutcome {
  runId: string;
  runNumber: number;
  machineCount: number;
  serversCreated: number;
  serversUpdated: number;
  factsWritten: number;
  legacyFactsRemoved: number;
  staleFactsRemoved: number;
  warnings: string[];
}

export interface MigrateContextUpdate {
  tenantId?: string;
  subscriptionId?: string;
  resourceGroup?: string;
  assessmentProjectName?: string;
  applianceName?: string;
  applianceStatus?: string;
  authMode?: string;
  discoveryStartedAt?: string;
}

/** Appliance checklist ladder, in order (RFC §4.2). */
export const APPLIANCE_LADDER = [
  'not-started',
  'key-generated',
  'registered',
  'discovery-running',
  'ready',
] as const;

export async function listMigrateRuns(
  projectId: string,
  customerId: string,
  token: string,
): Promise<MigrateRun[]> {
  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/migrate-runs?customerId=${encodeURIComponent(customerId)}`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Failed to list assessment runs: ${res.status}`);
  return res.json();
}

/**
 * Multipart upload — Business Case .xlsx, strategy-bundle .zip, or assessed-machines
 * .json from the migrate~run skill. Deliberately a bare fetch with FormData and NO
 * Content-Type header: the browser must set the multipart boundary itself.
 */
export async function importMigrateRunFile(
  projectId: string,
  customerId: string,
  file: File,
  token: string,
): Promise<ImportOutcome> {
  const form = new FormData();
  form.append('customerId', customerId);
  form.append('file', file);

  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/migrate-runs/import`),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    },
  );
  if (!res.ok) {
    let message = `Import failed: ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch { /* non-JSON error body — keep the status message */ }
    throw new Error(message);
  }
  return res.json();
}

/**
 * Run diff (RFC §4.6): the stored ingest-time diff vs the superseded run by default, or
 * an ad-hoc comparison against any sibling run via `against`.
 */
export async function getRunDiff(
  projectId: string,
  runId: string,
  customerId: string,
  token: string,
  against?: string,
): Promise<MigrateRunDiff> {
  const query = new URLSearchParams({ customerId });
  if (against) query.set('against', against);

  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/migrate-runs/${encodeURIComponent(runId)}/diff?${query.toString()}`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    let message = `Failed to load run diff: ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch { /* non-JSON error body — keep the status message */ }
    throw new Error(message);
  }
  return res.json();
}

// ── Workload auto-generation (RFC §4.5) ──

/** One proposed workload derived from a single assessed server in a run. */
export interface WorkloadCandidate {
  serverKey: string;
  serverId: string;
  name: string;
  suggestedType: string;
  targetAzureSku?: string | null;
  estimatedMonthlyCost?: number | null;
  currency: string;
  readiness?: string | null;
  confidencePercent?: number | null;
  confidenceStars?: number | null;
  alreadyCatalogued: boolean;
  nameCollision: boolean;
}

export interface WorkloadCandidatesResult {
  runId: string;
  currency: string;
  candidates: WorkloadCandidate[];
}

/** One chosen candidate to catalogue, with an optional workload-type override. */
export interface WorkloadAcceptItem {
  serverKey: string;
  type?: string;
}

export interface WorkloadAcceptOutcome {
  created: number;
  skippedNameCollision: number;
  skippedAlreadyCatalogued: number;
  skippedUnknownServer: number;
  failedLinks: number;
  totalMonthlyCost?: number | null;
  currency: string;
  workloadIds: string[];
}

/** Preview the workload candidates for a run (read-only) — RFC §4.5. */
export async function getWorkloadCandidates(
  projectId: string,
  runId: string,
  customerId: string,
  token: string,
): Promise<WorkloadCandidatesResult> {
  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/migrate-runs/${encodeURIComponent(runId)}/workload-candidates?customerId=${encodeURIComponent(customerId)}`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) {
    let message = `Failed to load workload candidates: ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch { /* non-JSON error body — keep the status message */ }
    throw new Error(message);
  }
  return res.json();
}

/** Accept selected candidates: bulk-create workloads + back-link servers — RFC §4.5. */
export async function acceptWorkloadCandidates(
  projectId: string,
  runId: string,
  customerId: string,
  items: WorkloadAcceptItem[],
  token: string,
): Promise<WorkloadAcceptOutcome> {
  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/migrate-runs/${encodeURIComponent(runId)}/workloads?customerId=${encodeURIComponent(customerId)}`),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ items }),
    },
  );
  if (!res.ok) {
    let message = `Failed to create workloads: ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch { /* non-JSON error body — keep the status message */ }
    throw new Error(message);
  }
  return res.json();
}

export async function setMigrateContext(
  projectId: string,
  customerId: string,
  update: MigrateContextUpdate,
  token: string,
): Promise<MigrateContext> {
  const res = await fetch(
    apiUrl(`/api/projects/${encodeURIComponent(projectId)}/migrate-context?customerId=${encodeURIComponent(customerId)}`),
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(update),
    },
  );
  if (!res.ok) throw new Error(`Failed to update migrate context: ${res.status}`);
  return res.json();
}
